import React, { useEffect, useRef } from 'react';
import { FiX, FiAlertCircle, FiCheck, FiEdit2 } from 'react-icons/fi';
import type { CoaDependants } from '../coaTemplateApi';
import './GLChartOfAccountsTab.css';

// =============================================
// Changing (or clearing) the tax-return code (GIFI) of an account that is in use.
// Says what moves to the new code — posted lines and the balance, documents booked to
// the account, sub-accounts, parties / natures that point to it, and the account
// number when the books are numbered by code — and that closed years keep the code
// saved then. "Move to the new code" saves; "Re-edit" goes back to the form.
// =============================================

export interface TaxCodeChange {
  formCode: string;     // 'GIFI'
  from: string;         // old code
  to: string;           // new code ('' = cleared)
  fromName?: string;
  toName?: string;
}

interface Props {
  accountLabel: string;
  changes: TaxCodeChange[];
  dependants: CoaDependants | null;
  /** books numbered by code: the account number follows the code */
  numberByCode: boolean;
  newNumber?: string;
  onConfirm: () => void;
  onReEdit: () => void;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

const TaxCodeChangeModal: React.FC<Props> = ({ accountLabel, changes, dependants, numberByCode, newNumber, onConfirm, onReEdit }) => {
  const confirmRef = useRef<HTMLButtonElement>(null);
  useEffect(() => { confirmRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onReEdit(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onReEdit]);

  const d = dependants;
  const items: string[] = [];
  if (d) {
    const bal = Number(d.account.current_balance) || 0;
    if (d.journal_lines) items.push(`${plural(d.journal_lines, 'posted journal line')}${bal ? ` (balance ${bal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})` : ''}`);
    else if (bal) items.push(`the balance of ${bal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
    if (d.bank_txns) items.push(plural(d.bank_txns, 'bank transaction'));
    if (d.documents) items.push(plural(d.documents, 'document') + ' booked to it');
    if (d.sub_accounts) items.push(plural(d.sub_accounts, 'sub-account') + ' (they keep their own codes)');
    if (d.party_defaults) items.push(plural(d.party_defaults, 'vendor / customer default') + ' pointing to it');
    if (d.nature_links) items.push(plural(d.nature_links, 'nature of document') + ' booked to it');
  }
  const closed = d?.closed_years || [];

  return (
    <div className="coa-acct-overlay" onMouseDown={onReEdit}>
      <div className="coa-acct-modal" role="dialog" aria-modal="true" aria-labelledby="coa-taxchange-title"
        onMouseDown={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="coa-acct-head">
          <h3 id="coa-taxchange-title"><FiAlertCircle size={16} style={{ verticalAlign: 'middle', color: '#b45309' }} /> Change the tax code?</h3>
          <button type="button" className="coa-acct-close" onClick={onReEdit} title="Back to the form" aria-label="Close"><FiX size={18} /></button>
        </div>
        <div className="coa-acct-body">
          <p style={{ margin: '0 0 8px' }}><strong>{accountLabel}</strong></p>
          {changes.map(c => (
            <p key={c.formCode} style={{ margin: '0 0 8px' }}>
              {c.formCode}: <strong>{c.from}</strong>{c.fromName ? ` ${c.fromName}` : ''} →{' '}
              {c.to ? <><strong>{c.to}</strong>{c.toName ? ` ${c.toName}` : ''}</> : <em>no code</em>}
            </p>
          ))}
          {d === null ? (
            <p className="coa-field-hint">Could not check what uses this account.</p>
          ) : items.length > 0 ? (
            <>
              <p style={{ margin: '4px 0' }}>Everything on this account moves to the new code from now on:</p>
              <ul style={{ margin: '0 0 8px 18px', padding: 0 }}>
                {items.map(t => <li key={t}>{t}</li>)}
                {numberByCode && newNumber && <li>the account number becomes <strong>{newNumber}</strong> (your own number is kept)</li>}
              </ul>
            </>
          ) : (
            <p style={{ margin: '4px 0' }}>Nothing is booked to this account yet.{numberByCode && newNumber ? ` Its number becomes ${newNumber}.` : ''}</p>
          )}
          {closed.length > 0 && (
            <p className="coa-field-hint" style={{ margin: '4px 0 0' }}>
              Years already closed keep the code saved then ({closed.map(y => `${y.d}: ${y.line_code || 'no code'}`).join(', ')}).
            </p>
          )}
          <div className="coa-acct-foot">
            <button type="button" className="coa-btn-secondary" onClick={onReEdit}><FiEdit2 size={14} /> Re-edit</button>
            <button type="button" ref={confirmRef} className="coa-btn-primary" onClick={onConfirm}>
              <FiCheck size={15} /> Move to {changes.length === 1 && changes[0].to ? changes[0].to : 'the new code'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxCodeChangeModal;
